import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { resolveMedia, isPdfUrl } from "@/lib/api";
import { ArrowRight, Building2, ConstructionIcon, Users, Plus, Minus, Calendar, CalendarPlus, FileText } from "lucide-react";
import NewsletterForm from "@/components/NewsletterForm";
import NewsletterValueTeaser from "@/components/NewsletterValueTeaser";
import ContactForm from "@/components/ContactForm";
import RichText from "@/components/RichText";
import { API } from "@/lib/api";
import useSEO from "@/lib/seo";

const WALLPAPER = "/assets/wallpaper-detmold.jpg";
const SKYLINE = "/assets/skyline-grey.svg";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return ""; }
}

function formatStand(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("de-DE", { month: "long", year: "numeric" }); }
  catch { return ""; }
}

export default function Home() {
  const [home, setHome] = useState(null);
  const [news, setNews] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [termine, setTermine] = useState([]);
  useSEO({
    title: null, // use site default title
    description: "Offizielle Informationsplattform zum Quartier am Hornschen Tor in Detmold. Aktuelle Baufortschritte, News, Termine, Verkehrsinformationen und Kontaktmöglichkeiten.",
    path: "/",
  });

  useEffect(() => {
    api.get("/homepage").then((r) => setHome(r.data));
    api.get("/news", { params: { status: "published", limit: 3 } }).then((r) => setNews((r.data || []).slice(0, 3)));
    api.get("/faqs", { params: { only_active: true } }).then((r) => setFaqs((r.data || []).slice(0, 6)));
    api.get("/termine", { params: { only_active: true } }).then((r) => setTermine(r.data || []));
  }, []);

  const hero = home?.hero || {};
  const info = home?.info || { blocks: [] };
  const progress = home?.progress || {};
  const heroImg = resolveMedia(hero.image_url) || WALLPAPER;

  // Parse the status value (e.g. "35 %", "01 %") to a number for the progress bar
  const progressPct = (() => {
    const v = parseInt((hero.status_value || "").replace(/[^\d]/g, ""), 10);
    if (Number.isFinite(v)) return Math.max(0, Math.min(100, v));
    return null;
  })();

  return (
    <div className="bg-white" data-testid="home-page">
      {/* ===== HERO (full-bleed banner) ===== */}
      <section className="relative" data-testid="hero">
        <div className="relative w-full overflow-hidden" style={{ minHeight: 720 }}>
          {/* Background image — sharp, no overlay veil */}
          <img src={heroImg} alt="Detmold" className="absolute inset-0 w-full h-full object-cover" />
          {/* White skyline at the bottom — colored via CSS mask, inset from edges */}
          <div
            aria-hidden="true"
            className="skyline-white absolute bottom-0 left-6 right-6 sm:left-10 sm:right-10 lg:left-14 lg:right-14 aspect-[1912/296] pointer-events-none"
            style={{ "--skyline-url": `url(${SKYLINE})` }}
          />

          {/* Status card + "Schön wird's"-Bild — absolute on desktop, aligned to the title's top baseline */}
          <div className="hidden lg:flex items-stretch gap-5 absolute right-8 xl:right-16 top-44 z-10" data-testid="hero-status-group">
            <div className="bg-white shadow-[0_10px_32px_rgba(0,0,0,0.10)] border border-brand-line w-[290px]" data-testid="hero-status-card">
              <div className="px-6 py-5">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-neutral-500">
                  <span className="inline-block h-2 w-2 rounded-full bg-[hsl(var(--brand-green))]" />
                  {hero.status_label || "Aktueller Baufortschritt"}
                </div>
                <p className="mt-3 text-5xl font-semibold text-[hsl(var(--brand-green-strong))] leading-none tracking-tight">
                  {hero.status_value || "—"}
                </p>
                {progressPct !== null && (
                  <div className="mt-4 h-1.5 bg-neutral-100 overflow-hidden">
                    <div
                      className="h-full bg-[hsl(var(--brand-green))] transition-[width] duration-700 ease-out"
                      style={{ width: `${progressPct}%` }}
                      data-testid="hero-progress-bar"
                    />
                  </div>
                )}
                {progress.last_updated && (
                  <p className="mt-3 text-[11px] text-neutral-400">Stand: {formatStand(progress.last_updated)}</p>
                )}
              </div>
            </div>
            <img
              src="/assets/schoen-wirds.jpg"
              alt="Schön wird's. Für dich. Für Detmold."
              className="w-[170px] h-auto self-stretch object-contain shadow-[0_10px_32px_rgba(0,0,0,0.10)] border border-brand-line bg-white"
              draggable={false}
              data-testid="hero-schoen-wirds"
            />
          </div>

          {/* Text content */}
          <div className="container-narrow relative pt-24 sm:pt-32 lg:pt-44 pb-[40vw] sm:pb-[28vw] lg:pb-[18vw] xl:pb-[16vw]">
            <h1 className="hero-title-readable font-semibold text-[34px] sm:text-5xl lg:text-[60px] xl:text-[64px] leading-[1.05] tracking-tight text-neutral-900 max-w-2xl" data-testid="hero-title">
              {hero.title || "Quartier am Hornschen Tor Detmold"}
            </h1>

            {/* Status card — inline on mobile/tablet */}
            <div className="lg:hidden mt-6 bg-white border border-brand-line px-5 py-4 shadow-[0_4px_16px_rgba(0,0,0,0.06)] max-w-md" data-testid="hero-status-card-mobile">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-neutral-500">
                  <span className="inline-block h-2 w-2 rounded-full bg-[hsl(var(--brand-green))]" />
                  {hero.status_label || "Aktueller Baufortschritt"}
                </div>
                <p className="text-2xl font-semibold text-[hsl(var(--brand-green-strong))] leading-none">
                  {hero.status_value || "—"}
                </p>
              </div>
              {progressPct !== null && (
                <div className="h-1.5 bg-neutral-100 overflow-hidden">
                  <div
                    className="h-full bg-[hsl(var(--brand-green))]"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              )}
              {progress.last_updated && (
                <p className="mt-2 text-[11px] text-neutral-400">Stand: {formatStand(progress.last_updated)}</p>
              )}
            </div>

            <div className="mt-6 lg:mt-7 block bg-white/80 backdrop-blur-sm px-4 sm:px-5 py-3 border border-white/60 text-sm sm:text-base lg:text-lg text-neutral-800 max-w-xl leading-relaxed shadow-[0_2px_12px_rgba(0,0,0,0.04)]" data-testid="hero-subtitle">
              <RichText html={hero.subtitle} />
            </div>
            <div className="mt-8 lg:mt-10">
              <Link
                to={hero.cta_primary_link || "/informationen"}
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white pl-6 pr-5 py-3.5 rounded-full hover:bg-[hsl(var(--brand-green-strong))] transition-colors text-sm font-medium"
              >
                {hero.cta_primary_label || "Mehr zum Projekt"}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== NEWS ===== */}
      <section className="container-narrow pt-12 sm:pt-16 lg:pt-24 pb-12" data-testid="home-news">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8 sm:mb-10">
          <h2 className="font-semibold text-2xl sm:text-3xl lg:text-4xl">Aktuelle News</h2>
          <Link to="/news" className="inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--brand-green-strong))] hover:underline" data-testid="news-show-all">
            Alle Nachrichten ansehen <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {news.length === 0 ? (
          <div className="border border-dashed border-brand-line p-12 text-center text-neutral-500">
            Aktuell sind noch keine Meldungen veröffentlicht.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
            {news.map((n) => (
              <Link key={n.id} to={`/news/${n.slug}`} className="group block" data-testid={`news-card-${n.slug}`}>
                <div className="aspect-[4/3] overflow-hidden bg-neutral-100 mb-6 flex items-center justify-center relative">
                  {n.image_url ? (
                    isPdfUrl(n.image_url) ? (
                      n.image_preview_url ? (
                        <>
                          <img src={resolveMedia(n.image_preview_url)} alt={n.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                          <span className="absolute top-3 left-3 bg-[hsl(var(--brand-green-dark))] text-white text-[10px] uppercase tracking-wider px-2 py-0.5 inline-flex items-center gap-1"><FileText className="h-3 w-3" />PDF</span>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center text-neutral-500 gap-2 px-4 text-center">
                          <FileText className="h-10 w-10" strokeWidth={1.5} />
                          <span className="text-[10px] uppercase tracking-wider">PDF-Dokument</span>
                        </div>
                      )
                    ) : (
                      <img src={resolveMedia(n.image_url)} alt={n.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                    )
                  ) : (
                    <span className="text-neutral-300 text-xs">Kein Bild</span>
                  )}
                </div>
                <p className="text-xs font-medium text-[hsl(var(--brand-green-strong))]">
                  {formatDate(n.date)}
                </p>
                <h3 className="mt-3 font-semibold text-2xl sm:text-[1.65rem] leading-tight group-hover:text-[hsl(var(--brand-green-strong))] transition-colors">
                  {n.title}
                </h3>
                {n.teaser && <p className="mt-4 text-sm text-neutral-600 line-clamp-3 leading-relaxed">{n.teaser}</p>}
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--brand-green-strong))]">
                  Mehr erfahren <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ===== TERMINE ===== */}
      <TermineSection items={termine} />

      {/* ===== ALLGEMEINE INFORMATIONEN ===== */}
      <section id="baufortschritt" className="container-narrow pt-8 sm:pt-12 pb-12 sm:pb-16 scroll-mt-24" data-testid="home-info">
        <h2 className="font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 sm:mb-8">Allgemeine Informationen</h2>
        <div className="bg-white border border-brand-line">
          <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-brand-line">
            <InfoCol
              icon={Building2}
              heading={(info.blocks?.[0]?.heading) || "Über das Projekt"}
              body={(info.blocks?.[0]?.body) || "Erfahren Sie mehr über die Ziele, die Planung und die Vision des neuen Quartiers am Hornschen Tor."}
              to="/informationen"
            />
            <InfoCol
              icon={ConstructionIcon}
              heading={(info.blocks?.[1]?.heading) || "Baufortschritt"}
              body={(info.blocks?.[1]?.body) || "Aktuelle Informationen zum Stand der Bauarbeiten, Meilensteinen und kommenden Schritten."}
              to="/informationen"
            />
            <InfoCol
              icon={Users}
              heading={(info.blocks?.[2]?.heading) || "Beteiligung & Kontakt"}
              body={(info.blocks?.[2]?.body) || "Ihre Meinung ist uns wichtig. Informieren Sie sich über Beteiligungsmöglichkeiten und Ansprechpartner."}
              to="/partner"
            />
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="container-narrow pt-8 sm:pt-12 pb-12 sm:pb-16" data-testid="home-faq">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6 sm:mb-8">
          <h2 className="font-semibold text-2xl sm:text-3xl lg:text-4xl">Fragen & Antworten</h2>
          <Link to="/faq" className="inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--brand-green-strong))] hover:underline" data-testid="faq-show-all">
            Alle Fragen anzeigen <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {faqs.length === 0 ? (
          <div className="border border-dashed border-brand-line p-10 text-center text-neutral-500">
            Es wurden noch keine Fragen veröffentlicht.
          </div>
        ) : (
          <div className="bg-white border border-brand-line">
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-brand-line">
              <FaqCol items={faqs.slice(0, Math.ceil(faqs.length / 2))} />
              <FaqCol items={faqs.slice(Math.ceil(faqs.length / 2))} />
            </div>
          </div>
        )}
      </section>

      {/* ===== NEWSLETTER ===== */}
      <section id="newsletter" className="container-narrow pt-12 pb-12 scroll-mt-24" data-testid="home-newsletter">
        <NewsletterValueTeaser />
        <div className="mt-12">
          <NewsletterForm />
        </div>
      </section>

      {/* ===== KONTAKT / BÜRGER-ANLIEGEN ===== */}
      <section id="kontakt" className="bg-neutral-50/70 border-t border-brand-line scroll-mt-24" data-testid="home-kontakt">
        <div className="container-narrow py-20 lg:py-24 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-3">Kontakt</p>
          <h2 className="font-semibold text-3xl sm:text-4xl mb-3">Haben Sie ein Anliegen?</h2>
          <p className="text-neutral-600 mb-10 leading-relaxed">
            Fragen, Anregungen oder Hinweise zum Bauvorhaben? Wir nehmen Ihr Anliegen entgegen und leiten es an die zuständige Stelle weiter.
          </p>
          <ContactForm compact />
        </div>
      </section>
    </div>
  );
}

function InfoCol({ icon: Icon, heading, body, to }) {
  return (
    <div className="p-8 lg:p-10">
      <Icon className="h-10 w-10 text-[hsl(var(--brand-green-strong))] mb-6" strokeWidth={1.4} />
      <h3 className="font-semibold text-xl mb-3">{heading}</h3>
      <RichText html={body} className="prose-quartier text-sm text-neutral-600 leading-relaxed mb-6" />
      <Link to={to} className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--brand-green-strong))] hover:underline">
        Mehr erfahren <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

function FaqCol({ items }) {
  return (
    <div className="divide-y divide-brand-line">
      {items.map((f) => <FaqRow key={f.id} item={f} />)}
    </div>
  );
}

function FaqRow({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`faq-item-${item.id}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 hover:bg-neutral-50 transition-colors"
      >
        <span className="text-sm sm:text-[15px] text-neutral-800">{item.question}</span>
        <span className="text-[hsl(var(--brand-green-strong))] shrink-0">
          {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5 -mt-1 text-sm text-neutral-600 leading-relaxed">
          <RichText html={item.answer} className="prose-quartier" />
        </div>
      )}
    </div>
  );
}

/* ===== Termine (Baustellen-Termine) ===== */
function formatTerminDate(iso) {
  if (!iso) return { day: "", month: "", year: "", long: "" };
  try {
    const d = new Date(iso);
    const day = d.toLocaleDateString("de-DE", { day: "2-digit" });
    const month = d.toLocaleDateString("de-DE", { month: "short" }).replace(".", "");
    const year = d.toLocaleDateString("de-DE", { year: "numeric" });
    const long = d.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
    return { day, month, year, long };
  } catch { return { day: "", month: "", year: "", long: "" }; }
}

function TermineSection({ items }) {
  if (!items || items.length === 0) return null;
  // Chronologisch sortieren: kommende zuerst (aufsteigend), dann vergangene (absteigend)
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const parse = (t) => { const d = new Date(t.date); d.setHours(0, 0, 0, 0); return d; };
  const upcoming = items.filter((t) => parse(t) >= today).sort((a, b) => parse(a) - parse(b));
  const past = items.filter((t) => parse(t) < today).sort((a, b) => parse(b) - parse(a));
  const ordered = [...upcoming, ...past].slice(0, 8);

  return (
    <section id="termine" className="container-narrow pt-8 sm:pt-12 pb-12 sm:pb-16 scroll-mt-24" data-testid="home-termine">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6 sm:mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-2 inline-flex items-center gap-1.5">
            <Calendar className="h-3 w-3" /> Bautermine
          </p>
          <h2 className="font-semibold text-2xl sm:text-3xl lg:text-4xl">Termine</h2>
        </div>
      </div>

      <ol className="bg-white border border-brand-line divide-y divide-brand-line">
        {ordered.map((t) => {
          const d = formatTerminDate(t.date);
          const isPast = parse(t) < today;
          return (
            <li
              key={t.id}
              data-testid={`termin-${t.id}`}
              className={`flex flex-col sm:flex-row gap-4 sm:gap-6 px-5 sm:px-7 py-5 sm:py-6 ${isPast ? "opacity-60" : ""}`}
            >
              <div
                className={`flex-shrink-0 w-full sm:w-24 flex sm:flex-col items-center sm:items-start justify-start gap-2 sm:gap-0.5 border-l-4 pl-3 sm:pl-4 ${
                  isPast ? "border-neutral-300" : "border-[hsl(var(--brand-green))]"
                }`}
              >
                <span className="text-3xl sm:text-4xl font-semibold leading-none tracking-tight text-[hsl(var(--brand-green-strong))]">
                  {d.day}
                </span>
                <span className="text-xs uppercase tracking-[0.14em] text-neutral-500 sm:mt-1">
                  {d.month} {d.year}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg sm:text-xl text-neutral-900 leading-snug">{t.title}</h3>
                {isPast && <p className="text-[10px] uppercase tracking-wider text-neutral-400 mt-0.5">Bereits durchgeführt</p>}
                {t.body && <RichText html={t.body} className="prose-quartier text-sm text-neutral-600 mt-2" />}
                {!isPast && (
                  <a
                    href={`${API}/termine/${t.id}/ics`}
                    download
                    data-testid={`termin-ics-${t.id}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-[hsl(var(--brand-green-strong))] hover:text-[hsl(var(--brand-green-dark))] hover:underline transition-colors"
                  >
                    <CalendarPlus className="h-3.5 w-3.5" />
                    In Kalender speichern
                  </a>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
